import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { clsx } from 'clsx';
import * as React from 'react';
import { RequiredLabel } from './RequiredLabel';
export function SliderField({ label, required = false, value, onChange, min = 1, max = 10, step = 1, values, defaultValue = 5, helperText, error, suppressErrorMessage = false, showRequiredText = false, disabled = false, className, minLabel, centerLabel, maxLabel, valueLabel, valueDescription, supportingText, }) {
    const id = React.useId();
    const hasError = Boolean(error);
    const describedBy = [hasError ? `${id}-error` : null, helperText ? `${id}-helper` : null]
        .filter(Boolean)
        .join(' ') || undefined;
    const isUnset = value === null;
    const current = value ?? defaultValue;
    const ticks = React.useMemo(() => {
        if (values && values.length > 0) {
            return [...new Set(values)].sort((a, b) => a - b);
        }
        const nextTicks = [];
        for (let tickValue = min; tickValue <= max; tickValue += step) {
            nextTicks.push(tickValue);
        }
        return nextTicks;
    }, [values, min, max, step]);
    const firstTick = ticks[0] ?? min;
    const lastTick = ticks[ticks.length - 1] ?? max;
    const getTickLabel = React.useCallback((tick) => {
        return String(tick);
    }, []);
    const getTickAriaLabel = React.useCallback((tick) => {
        if (tick === firstTick && minLabel) {
            return `${tick} (${minLabel})`;
        }
        if (tick === lastTick && maxLabel) {
            return `${tick} (${maxLabel})`;
        }
        return String(tick);
    }, [firstTick, lastTick, minLabel, maxLabel]);
    // The nodes are evenly flex-spaced, so the fill has to be INDEX-based to line up
    // with the selected node (for a uniform min/max/step scale the two are the same
    // number; for explicit `values` like [1, 3, 6, 12] they are not). A stored value
    // that is no longer a tick falls back to its clamped proportional position.
    const selectedPercent = React.useMemo(() => {
        if (ticks.length <= 1) {
            return 0;
        }
        const index = ticks.indexOf(current);
        if (index >= 0) {
            return (index / (ticks.length - 1)) * 100;
        }
        if (lastTick === firstTick) {
            return 0;
        }
        const clamped = Math.min(Math.max(current, firstTick), lastTick);
        return ((clamped - firstTick) / (lastTick - firstTick)) * 100;
    }, [current, ticks, firstTick, lastTick]);
    const nodeRefs = React.useRef(new Map());
    const focusTick = React.useCallback((tick) => {
        requestAnimationFrame(() => {
            nodeRefs.current.get(tick)?.focus();
        });
    }, []);
    const handleNodeKeyDown = React.useCallback((event, tick) => {
        // Neighbor lookup by INDEX so irregular scales ([1, 3, 6, 12]) step to the
        // next real tick; for uniform scales this is identical to the old +-step math.
        const index = ticks.indexOf(tick);
        if (index < 0) {
            return;
        }
        let nextTick = null;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowUp':
                nextTick = ticks[Math.min(index + 1, ticks.length - 1)];
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                nextTick = ticks[Math.max(index - 1, 0)];
                break;
            case 'Home':
                nextTick = firstTick;
                break;
            case 'End':
                nextTick = lastTick;
                break;
            default:
                return;
        }
        event.preventDefault();
        if (nextTick !== tick) {
            onChange(nextTick);
        }
        focusTick(nextTick);
    }, [focusTick, ticks, firstTick, lastTick, onChange]);
    return (_jsxs("div", { className: clsx('slider-field', className), children: [label && (_jsxs("div", { className: "slider-field__header", children: [_jsxs("div", { className: "slider-field__label-row", children: [_jsx("label", { id: `${id}-label`, className: "slider-field__label", children: _jsx(RequiredLabel, { label: label, required: required, showRequiredText: showRequiredText || hasError }) }), _jsx("span", { className: "slider-field__value", "aria-live": "polite", children: !isUnset ? (_jsxs(_Fragment, { children: [current, valueLabel ? (_jsxs("span", { className: "slider-field__value-label", children: ["(", valueLabel, ")"] })) : null] })) : null })] }), helperText && (_jsx("p", { id: `${id}-helper`, className: "slider-field__helper", children: helperText }))] })), _jsxs("div", { className: "slider-field__control", role: "radiogroup", "aria-labelledby": label ? `${id}-label` : undefined, "aria-describedby": describedBy, "aria-disabled": disabled || undefined, "data-unset": isUnset ? 'true' : undefined, children: [_jsx("div", { className: "slider-field__track", "aria-hidden": "true", children: _jsx("div", { className: "slider-field__range", style: { width: `${selectedPercent}%` } }) }), _jsx("div", { className: "slider-field__nodes", children: ticks.map((tick) => {
                            const isSelected = tick === current;
                            return (_jsx("button", { ref: (node) => {
                                    nodeRefs.current.set(tick, node);
                                }, type: "button", role: "radio", "aria-checked": isSelected, "aria-label": `${label ?? 'Value'} ${getTickAriaLabel(tick)}`, tabIndex: isSelected ? 0 : -1, className: "slider-field__node", "data-selected": isSelected && !isUnset ? 'true' : undefined, "data-default": isSelected && isUnset ? 'true' : undefined, disabled: disabled, onClick: () => onChange(tick), onKeyDown: (event) => handleNodeKeyDown(event, tick), children: _jsx("span", { className: "slider-field__node-label", children: getTickLabel(tick) }) }, tick));
                        }) })] }), minLabel || centerLabel || maxLabel ? (_jsxs("div", { className: "slider-field__scale", "aria-hidden": "true", children: [_jsx("span", { className: "slider-field__scale-label slider-field__scale-label--start", children: minLabel }), _jsx("span", { className: "slider-field__scale-label slider-field__scale-label--center", children: centerLabel }), _jsx("span", { className: "slider-field__scale-label slider-field__scale-label--end", children: maxLabel })] })) : null, !isUnset && valueDescription ? (_jsx("p", { id: `${id}-helper`, className: "slider-field__helper slider-field__helper--emphasis", children: valueDescription })) : null, supportingText ? _jsx("p", { className: "slider-field__supporting", children: supportingText }) : null, hasError && !suppressErrorMessage && (_jsx("p", { id: `${id}-error`, className: "slider-field__error", children: error }))] }));
}
//# sourceMappingURL=SliderField.js.map